import { useState, useEffect } from "react";
import { SlackIcon } from "@/components/icons/SlackIcon";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/ui/Modal";
import { SlackChannelSelector } from "./SlackChannelSelector";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { router } from "@inertiajs/react";

interface ConfigureSlackModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  awsAccountUuid?: string;
  githubRepositoryUuid?: string;
  existingSetting?: {
    id: number;
    destination: string;
    metadata: {
      channel_name: string;
    };
  };
}

export function ConfigureSlackModal({
  isOpen,
  onClose,
  workspaceSlug,
  awsAccountUuid,
  githubRepositoryUuid,
  existingSetting,
}: ConfigureSlackModalProps) {
  const [selectedChannelId, setSelectedChannelId] = useState<string>(
    existingSetting?.destination || "",
  );
  const [selectedChannelName, setSelectedChannelName] = useState<string>(
    existingSetting?.metadata.channel_name || "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  // Reset state when modal opens or existingSetting changes
  useEffect(() => {
    if (isOpen) {
      setSelectedChannelId(existingSetting?.destination || "");
      setSelectedChannelName(existingSetting?.metadata.channel_name || "");
    }
  }, [isOpen, existingSetting]);

  const handleChannelChange = (channelId: string, channelName: string) => {
    setSelectedChannelId(channelId);
    setSelectedChannelName(channelName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedChannelId) {
      showToast("error", "Please select a Slack channel");
      return;
    }

    setIsSubmitting(true);

    try {
      const baseUrl = awsAccountUuid
        ? `/workspaces/${workspaceSlug}/aws_accounts/${awsAccountUuid}/notification_settings`
        : `/workspaces/${workspaceSlug}/github_repositories/${githubRepositoryUuid}/notification_settings`;

      const url = existingSetting
        ? `${baseUrl}/${existingSetting.id}`
        : baseUrl;

      const method = existingSetting ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          notification_setting: {
            platform: "slack",
            destination: selectedChannelId,
            metadata: {
              channel_name: selectedChannelName,
            },
          },
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to save notification settings");
      }

      showToast(
        "success",
        existingSetting
          ? "Slack notification settings updated"
          : "Slack notification settings saved",
      );

      onClose();
      router.reload();
    } catch (error) {
      showToast(
        "error",
        error instanceof Error
          ? error.message
          : "Failed to save notification settings",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!existingSetting) return;

    setIsDeleting(true);

    try {
      const baseUrl = awsAccountUuid
        ? `/workspaces/${workspaceSlug}/aws_accounts/${awsAccountUuid}/notification_settings`
        : `/workspaces/${workspaceSlug}/github_repositories/${githubRepositoryUuid}/notification_settings`;

      const response = await fetch(
        `${baseUrl}/${existingSetting.id}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to remove notification setting");
      }

      showToast("success", "Slack notification setting removed");

      onClose();
      router.reload();
    } catch (error) {
      showToast(
        "error",
        error instanceof Error
          ? error.message
          : "Failed to remove notification setting",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      canClose={!isSubmitting && !isDeleting}
    >
      <form onSubmit={handleSubmit}>
        <ModalHeader
          icon={<SlackIcon className="size-6 text-purple-600" />}
          title="Configure Slack Notifications"
          subtitle={awsAccountUuid
            ? "Select a Slack channel to receive notifications for this AWS account."
            : "Select a Slack channel to receive notifications for this Git repository."}
        />

        <ModalBody>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="slack-channel"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Slack Channel
              </label>
              <SlackChannelSelector
                workspaceSlug={workspaceSlug}
                value={selectedChannelId}
                onValueChange={handleChannelChange}
                placeholder="Select a channel"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="flex items-center justify-between w-full">
            <div>
              {existingSetting && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleRemove}
                  disabled={isSubmitting || isDeleting}
                  className="border-red-300 text-red-600 hover:text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                >
                  {isDeleting ? "Removing..." : "Remove"}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                type="button"
                onClick={onClose}
                disabled={isSubmitting || isDeleting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isDeleting || !selectedChannelId}
                isLoading={isSubmitting}
                loadingText="Saving..."
                className="bg-purple-600 hover:bg-purple-700"
              >
                Save
              </Button>
            </div>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
